import { useCallback, useMemo, useState } from 'react';
import { Slice, SliceProps, VictoryChart, VictoryPie } from 'victory';
import { darkenColor, getProfitColor } from '../../../../../helpers/colors';
import {
  formatBalance,
  formatPercentage,
} from '../../../../../helpers/NumberFormatter';
import { getBotColor } from '../helpers';
import { SortingToggleOptions } from '../../../types';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setActiveSubscriptionId } from '../../../../../store/slices/subscription';
import { useSelector } from 'react-redux';
import { selectOverviewChartPercentage } from '../../../../../store/slices/ui';

interface Props {
  data: Array<{ x: string; y: number; value?: number; subscriptionId: number }>;
  activeToggleOption: SortingToggleOptions;
  showAllData: boolean | undefined;
  width: number;
  height: number;
}

const PieChart = ({
  data,
  activeToggleOption,
  showAllData,
  width,
  height,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isRelative = useSelector(selectOverviewChartPercentage);

  const diversityColorScale = useMemo(
    () => data.map((datum) => getBotColor(datum.x)),
    [data],
  );

  const profitColorScale = useMemo(
    () => data.map((datum) => getProfitColor(datum.value ?? datum.y)),
    [data],
  );

  const colorScale = useMemo(
    () =>
      activeToggleOption === SortingToggleOptions.Diversity ||
      activeToggleOption === SortingToggleOptions.TotalValue
        ? diversityColorScale
        : profitColorScale,
    [activeToggleOption, diversityColorScale, profitColorScale],
  );

  const totalValue = useMemo(() => {
    return data.reduce((acc, curr) => acc + Math.abs(curr.y), 0);
  }, [data]);

  const createLabel = useCallback(
    (datum: any) => {
      return `${datum.x}\n${
        isRelative || activeToggleOption.includes('Asset')
          ? formatPercentage(datum.labelValue ?? datum.value ?? datum.y, 1)
          : formatBalance(datum.value ?? datum.y, true)
      }`;
    },
    [activeToggleOption, isRelative],
  );

  const labels = useMemo(
    () =>
      data.map((datum) => {
        const label = createLabel(datum);
        return datum.y / totalValue > 0.023 ? label : '';
      }),
    [createLabel, data, totalValue],
  );

  const maxValue = useMemo(
    () => Math.max(...data.map((d) => d.y || 0)),
    [data],
  );
  const minValue = useMemo(
    () => Math.min(...data.map((d) => d.y || 0)),
    [data],
  );

  const getRadius = useCallback(
    ({ datum }: any) => {
      const base = (height / 2.5) * (showAllData ? 1.2 : 1);

      const diffOriginal = maxValue - minValue;
      const a = datum.y;
      const aPerc = (a - minValue) / diffOriginal;
      const ratio = 0.65 * (showAllData ? 0.9 : 1);
      return base * Math.max(0.5, Math.min(1, ratio + ratio * aPerc));
    },
    [height, showAllData, maxValue, minValue],
  );

  const getLabelRadius = useCallback(
    ({ datum }: any) => getRadius({ datum }) + 10,
    [getRadius],
  );

  const style = useMemo(
    () => ({
      // data: {
      //   stroke: 'gray',
      //   strokeWidth: 0.2,
      // },
      labels: {
        fontSize: 8,
        fill: 'white',
        fontWeight: 700,
        opacity: showAllData ? 1 : 0,
      },
    }),
    [showAllData],
  );

  return (
    <VictoryPie
      key={`${isRelative}`}
      width={width * (showAllData ? 1 : 1)}
      height={height * (showAllData ? 1 : 1)}
      data={data}
      colorScale={colorScale}
      padAngle={2}
      innerRadius={20}
      style={style}
      cornerRadius={2}
      labels={labels}
      labelRadius={getLabelRadius}
      radius={getRadius}
      events={[
        {
          target: 'data',
          eventHandlers: {
            onClick: () => {
              return [
                {
                  target: 'data',
                  mutation: async ({ datum }) => {
                    if (!showAllData) {
                      return;
                    }

                    void dispatch(
                      setActiveSubscriptionId(datum.subscriptionId),
                    );
                    navigate('/sub');
                    return null; // No visual mutation on click
                  },
                },
              ];
            },
            onMouseOver: () => {
              return [
                {
                  target: 'data',
                  mutation: ({ datum, index }) => {
                    return {
                      radius: getRadius({ datum }) + 5, // Increase radius on hover
                      style: { fill: darkenColor(colorScale[index]) },
                    };
                  },
                },
                {
                  target: 'labels',
                  mutation: ({ datum }) => ({
                    text: createLabel(datum),
                    style: { ...style.labels, fontSize: 9, opacity: 1 },
                  }),
                },
              ];
            },
            onMouseOut: () => {
              return [
                {
                  target: 'data',
                  mutation: ({ datum }) => ({ radius: getRadius({ datum }) }), // Reset radius
                },
                {
                  target: 'labels',
                  mutation: () => ({
                    style: style.labels,
                  }),
                },
              ];
            },
          },
        },
      ]}
    />
  );
};

export default PieChart;
